/**
 * The only API path a post-login redirect may target: the account export download (T772). Native store builds
 * (D-LAUNCH-1) open `/login?redirectTo=%2Fapi%2Fv1%2Fexport` instead of the priced account page. Every other `/api/`
 * path falls back to the home page, so the redirect cannot be aimed at an arbitrary endpoint.
 */
const EXPORT_PATH = "/api/v1/export"

/** Also checks the decoded form, so "/api%2Fv1%2Fme" cannot slip past the filter. */
function isApiPath(pathname: string) {
  let decoded = pathname
  try {
    decoded = decodeURIComponent(pathname)
  } catch {
    return true
  }
  return [pathname, decoded].some((p) => /^\/+api(\/|$)/i.test(p))
}

export function getRedirectUrl(redirectTo: string = "/") {
  const url = new URL(redirectTo, "https://base")
  // Collapse leading slashes: "/.//host" normalises to "//host", which a browser reads as another origin.
  const pathname = url.pathname.replace(/^\/{2,}/, "/")
  // A non-special scheme ("javascript:x", "mailto:a@b") leaves a pathname without a leading slash.
  if (!pathname.startsWith("/")) return "/"
  if (pathname === EXPORT_PATH) return EXPORT_PATH
  if (isApiPath(pathname)) return "/"
  return pathname + url.search
}

/** True when a sanitised post-login target is the export download (the login page then stays open, see T780). */
export function isExportRedirect(redirectTo?: string) {
  return redirectTo === EXPORT_PATH
}

/**
 * Router redirect options for a post-login target. The export is a file download served by an API route, so it
 * needs a full document navigation (`reloadDocument`); a client-side navigation would not start the download.
 */
export function getRedirectTarget(redirectTo?: string): { to: string; reloadDocument?: true } {
  const to = getRedirectUrl(redirectTo)
  return isExportRedirect(to) ? { to, reloadDocument: true } : { to }
}
